import React, { useState, useMemo } from "react";
import { observer } from "mobx-react";
import "./index.css";
import GlobalState from "../../../stores/GlobalState";

const STEP = 0.05;

function roundToStep(value, step = STEP) {
  return Math.round(value / step) * step;
}
function sameStep(a, b, step = STEP) {
  return Math.abs(roundToStep(a, step) - roundToStep(b, step)) < 1e-9;
}

function safeNum(x, fallback = 0) {
  return typeof x === "number" && Number.isFinite(x) ? x : fallback;
}

function makeTargetConfusion(cm, target) {
  // cm = [[a00, a01],[a10, a11]] where:
  // a00 = actual0 pred0, a01 = actual0 pred1, a10 = actual1 pred0, a11 = actual1 pred1
  const a00 = safeNum(cm?.[0]?.[0], 0);
  const a01 = safeNum(cm?.[0]?.[1], 0);
  const a10 = safeNum(cm?.[1]?.[0], 0);
  const a11 = safeNum(cm?.[1]?.[1], 0);

  // Для target=1 (звичний позитивний клас=1)
  // TP=a11, FN=a10, FP=a01, TN=a00
  if (target === 1) {
    const tp = a11;
    const fn = a10;
    const fp = a01;
    const tn = a00;

    const positivesTotal = tp + fn; // actual 1
    const negativesTotal = tn + fp; // actual 0

    return {
      tp,
      fp,
      tn,
      fn,
      positivesTotal,
      negativesTotal,
      truePositivesPercentage: positivesTotal ? tp / positivesTotal : 0,
      falseNegativesPercentage: positivesTotal ? fn / positivesTotal : 0,
      trueNegativesPercentage: negativesTotal ? tn / negativesTotal : 0,
      falsePositivesPercentage: negativesTotal ? fp / negativesTotal : 0,
    };
  }

  // Для target=0 (позитивний клас=0)
  // TP0 = a00, FN0 = a01, FP0 = a10, TN0 = a11
  const tp = a00;
  const fn = a01;
  const fp = a10;
  const tn = a11;

  const positivesTotal = tp + fn; // actual 0
  const negativesTotal = tn + fp; // actual 1

  return {
    tp,
    fp,
    tn,
    fn,
    positivesTotal,
    negativesTotal,
    truePositivesPercentage: positivesTotal ? tp / positivesTotal : 0,
    falseNegativesPercentage: positivesTotal ? fn / positivesTotal : 0,
    trueNegativesPercentage: negativesTotal ? tn / negativesTotal : 0,
    falsePositivesPercentage: negativesTotal ? fp / negativesTotal : 0,
  };
}

const TabsTarget = observer(({ lower, upper }) => {
  const [activetab, setActivetab] = useState(0);

  const meta = GlobalState.activemodel?.lastTrain?.metadata;
  const doubleThresholdsData = meta?.doubleThresholdsData || [];

  const selectedDouble = useMemo(() => {
    if (!Array.isArray(doubleThresholdsData) || !doubleThresholdsData.length) return null;

    const l = roundToStep(Number(lower ?? 0));
    const u = roundToStep(Number(upper ?? 0));

    return (
      doubleThresholdsData.find(
        (o) => sameStep(o.lowerThreshold, l) && sameStep(o.upperThreshold, u)
      ) || null
    );
  }, [doubleThresholdsData, lower, upper]);

  const tabs = useMemo(() => {
    const cm = selectedDouble?.confusionMatrix;
    if (!cm) return [];

    // Будуємо 2 таби: target 0 і target 1
    const targets = [0, 1];

    return targets.map((t) => {
      const c = makeTargetConfusion(cm, t);

      return {
        z: "Target '" + t + "'",
        lines: [
          {
            title: "True Positives",
            type: true,
            procent: (c.truePositivesPercentage * 100).toFixed(0),
            col1: c.tp,
            col2: c.positivesTotal,
            text:
              "The Model correctly predicted " +
              c.tp +
              " of the " +
              c.positivesTotal +
              " rows in the test set where <span>Target<span> is <span>" +
              t +
              "<span>",
          },
          {
            title: "False Positives",
            type: false,
            procent: (c.falsePositivesPercentage * 100).toFixed(0),
            col1: c.fp,
            col2: c.negativesTotal,
            text:
              "It incorrectly predicted " +
              c.fp +
              " of the remaining " +
              c.negativesTotal +
              " rows as <span>" +
              t +
              "<span>",
          },
          {
            title: "True Negatives",
            type: true,
            procent: (c.trueNegativesPercentage * 100).toFixed(0),
            col1: c.tn,
            col2: c.negativesTotal,
            text:
              "The Model correctly predicted " +
              c.tn +
              " of the " +
              c.negativesTotal +
              " rows in the test set where <span>Target<span> is not <span>" +
              t +
              "<span>",
          },
          {
            title: "False Negatives",
            type: false,
            procent: (c.falseNegativesPercentage * 100).toFixed(0),
            col1: c.fn,
            col2: c.positivesTotal,
            text:
              "It incorrectly predicted " +
              c.fn +
              " of the remaining " +
              c.positivesTotal +
              " rows as  not <span>" +
              t +
              "<span>",
          },
        ],
      };
    });
  }, [selectedDouble]);

  // якщо табів менше, ніж activetab (наприклад дані ще не прийшли) – фіксимо
  const safeActive = activetab < tabs.length ? activetab : 0;

  return (
    <div className="tabstarget">
      <div className="tabstarget_head">
        {tabs.map((n, id) => (
          <div
            key={`tab-head-${id}`}
            className={id == safeActive ? "tabstarget_head_tab active" : "tabstarget_head_tab"}
            onClick={() => setActivetab(id)}
          >
            {n.z}
          </div>
        ))}
      </div>

      <div className="tabstarget_body">
        {tabs.map((n, id) => (
          <div
            key={`tab-body-${id}`}
            className={id == safeActive ? "tabstarget_body_tab active" : "tabstarget_body_tab"}
          >
            {n.lines.map((x, i) => (
              <div key={`line-${id}-${i}`} className="tabstarget_body_tab_line">
                <div className="tabstarget_body_tab_line_col1">{x.title}</div>

                <div className="tabstarget_body_tab_line_col2">
                  <div className="zagruz_line">
                    <span
                      style={
                        x.procent >= 0 && x.procent <= 49
                          ? { width: x.procent + "%", background: "rgba(234, 67, 52, 1)" }
                          : x.procent > 49 && x.procent <= 59
                          ? { width: x.procent + "%", background: "rgba(235, 115, 27, 1)" }
                          : x.procent > 59 && x.procent <= 69
                          ? { width: x.procent + "%", background: "rgba(66, 133, 244, 1)" }
                          : x.procent > 69 && x.procent <= 79
                          ? { width: x.procent + "%", background: "rgba(65, 168, 177, 1)" }
                          : { width: x.procent + "%", background: "rgba(51, 167, 83, 1)" }
                      }
                    ></span>
                  </div>
                </div>

                <div className="tabstarget_body_tab_line_col3">
                  <span>{x.col1}</span>/{x.col2}
                </div>

                <div className="tabstarget_body_tab_line_col4">
                  <span
                    style={
                      x.procent >= 0 && x.procent <= 49
                        ? { color: "rgba(234, 67, 52, 1)" }
                        : x.procent > 49 && x.procent <= 59
                        ? { color: "rgba(235, 115, 27, 1)" }
                        : x.procent > 59 && x.procent <= 69
                        ? { color: "rgba(66, 133, 244, 1)" }
                        : x.procent > 69 && x.procent <= 79
                        ? { color: "rgba(65, 168, 177, 1)" }
                        : { color: "rgba(51, 167, 83, 1)" }
                    }
                  >
                    {x.procent}%
                  </span>
                </div>

                <div
                  className="tabstarget_body_tab_line_col5"
                  dangerouslySetInnerHTML={{ __html: x.text }}
                ></div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
});

export default TabsTarget;
